// Shared modal show/hide primitives. The admin / marketplace / AI /
// feedback zones each have ~1-5 full-screen overlays that are toggled
// via plain `.hidden` + `hidden` attribute pairs plus null-safety. The
// pattern is short (1-2 lines per site) but uniform, and centralising
// it gives every modal the same keyboard behaviour: focus moves in on
// open, Tab stays inside, Escape closes, and closing hands focus back to
// whatever opened it. Keep the API surface minimal.

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

interface OpenModal {
  opener: HTMLElement | null;
  onKeydown: (e: KeyboardEvent) => void;
  onHide?: () => void;
}

// Keyed by element rather than id: an overlay re-rendered under the same id
// is a new element and needs its own listener.
const openModals = new WeakMap<HTMLElement, OpenModal>();
// Most recently opened last; the one focus is held in.
const openStack: HTMLElement[] = [];
let focusGuardBound = false;

export interface ShowModalOptions {
  /** Runs once when the modal closes, whether by Escape or by a caller's
   *  hideModal() — cleanup that must not depend on which button was used. */
  onHide?: () => void;
}

/** Controls a keyboard user can reach inside `root`, in DOM order. Sections
 *  a modal switches off with `hidden` (loading states, spent forms) are out. */
function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
    .filter((el) => !el.closest('[hidden]'));
}

function onModalKeydown(id: string, el: HTMLElement, e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    // The page must not see this Escape too: the planner's own handler
    // would drop a half-drawn shape and reset the tool behind the modal.
    e.preventDefault();
    e.stopPropagation();
    hideModal(id);
    return;
  }
  if (e.key !== 'Tab') return;

  const items = focusableWithin(el);
  if (items.length === 0) {
    e.preventDefault();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  if (e.shiftKey && (active === first || active === el)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

/** Tab is trapped by the keydown handler, but focus can still get out: a
 *  control that hides itself mid-flow (a Start button giving way to a
 *  spinner) drops focus onto <body>, and the next Tab lands on the page
 *  behind the backdrop. Focus arriving anywhere outside the top modal is
 *  sent back in — unless it went to another dialog stacked on top of it. */
function guardFocus(e: FocusEvent): void {
  // Overlays removed from the page or hidden without hideModal() hold nothing.
  const holds = (el: HTMLElement) => openModals.has(el) && el.isConnected && !el.hidden;
  while (openStack.length && !holds(openStack[openStack.length - 1])) openStack.pop();
  const top = openStack[openStack.length - 1];
  const target = e.target;
  if (!top || !(target instanceof Element) || top.contains(target)) return;
  if (target.closest('[role="dialog"], [role="alertdialog"]')) return;
  (focusableWithin(top)[0] ?? top).focus();
}

/**
 * Reveal a modal overlay by id. Clears both `.hidden` and the `hidden`
 * attribute so the element participates in layout, and flips
 * `aria-hidden` to `false` so screen readers announce the new content.
 * Focus moves to the first control inside unless the caller has already
 * put it there. Returns the element for convenience chaining (focus,
 * query inside), or `null` if the id isn't mounted.
 */
export function showModal(id: string, options: ShowModalOptions = {}): HTMLElement | null {
  const el = document.getElementById(id);
  if (!el) return null;
  el.hidden = false;
  el.removeAttribute('hidden');
  el.setAttribute('aria-hidden', 'false');

  if (!openModals.has(el)) {
    const active = document.activeElement;
    const onKeydown = (e: KeyboardEvent) => onModalKeydown(id, el, e);
    el.addEventListener('keydown', onKeydown);
    openModals.set(el, {
      opener: active instanceof HTMLElement && active !== document.body ? active : null,
      onKeydown,
      onHide: options.onHide,
    });
  }
  if (!openStack.includes(el)) openStack.push(el);
  if (!focusGuardBound) {
    document.addEventListener('focusin', guardFocus);
    focusGuardBound = true;
  }

  // A click on plain text inside would otherwise send focus to <body>,
  // out of reach of the keydown listener above.
  if (!el.hasAttribute('tabindex')) el.tabIndex = -1;
  if (!el.contains(document.activeElement)) (focusableWithin(el)[0] ?? el).focus();
  return el;
}

/**
 * Hide a modal overlay by id. No-op if the id isn't mounted so callers
 * can invoke from cleanup paths without guarding. Focus returns to the
 * element that was focused when the modal opened, unless the user has
 * already moved it elsewhere.
 */
export function hideModal(id: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  const open = openModals.get(el);
  if (open) {
    openModals.delete(el);
    const at = openStack.indexOf(el);
    if (at >= 0) openStack.splice(at, 1);
    el.removeEventListener('keydown', open.onKeydown);
    const active = document.activeElement;
    // Before hiding, so focus never sits inside an aria-hidden subtree.
    if (open.opener?.isConnected && (!active || active === document.body || el.contains(active))) {
      open.opener.focus();
    }
  }
  el.hidden = true;
  el.setAttribute('hidden', '');
  el.setAttribute('aria-hidden', 'true');
  open?.onHide?.();
}

/**
 * Report whether a modal overlay is currently visible. Treats a
 * missing id as "not open" so polling callers (e.g. keyboard handlers
 * that close on Escape) don't need to guard for mount timing.
 */
export function isOpenModal(id: string): boolean {
  const el = document.getElementById(id);
  return !!el && !el.hidden;
}
